<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      sort-by="name"
      :search="search"
      class="elevation-0 general-table"
      @click:row="itemEdit"
      :loading="isLoading"
      :loading-text="$t('GLOBAL-LOADING-PLEASE_WAIT')"
    >
      <template v-slot:header.image="{ header }">
        <lang t="LHF-TBL-HEADER-IMAGE"></lang>
      </template>

      <template v-slot:header.name="{ header }">
        <lang t="LHF-TBL-HEADER-NAME"></lang>
      </template>

      <template v-slot:header.type="{ header }">
        <lang t="LHF-TBL-HEADER-TYPE"></lang>
      </template>

      <template v-slot:header.dimensions="{ header }">
        <lang t="LHF-TBL-HEADER-DIMENSIONS"></lang>
      </template>

      <template v-slot:header.material="{ header }">
        <lang t="LHF-TBL-HEADER-MATERIAL"></lang>
      </template>

      <template v-slot:footer.page-text="props" v-if="items.length > 0">
        <display-number :number="props.pageStart" />-<display-number :number="props.pageStop" /> {{ $t("of") }}
        <display-number :number="items.length" />
      </template>

      <template v-slot:top>
        <div class="d-flex align-center flex-wrap py-2 mb-3">
          <v-text-field
            clearable
            class="general-table__search"
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            :label="$t('GLOBAL-DO_SEARCH')"
            solo
            flat
            dense
            hide-details
          >
          </v-text-field>

          <v-spacer></v-spacer>

          <Button type="outside-label" icon="mdi-plus" :label="$t('LHF-NEW_LHF')" @click="itemAdd" />
        </div>
      </template>

      <template v-slot:item.image="{ item }">
        <v-img width="32px" height="32px" contain :src="$config.cdn + item.image">
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular size="16" width="2" indeterminate color="grey lighten-2"></v-progress-circular>
            </v-row>
          </template>
        </v-img>
      </template>

      <template v-slot:item.dimensions="{ item }"> {{ item.length_mm }}x{{ item.width_mm }}x{{ item.height_mm }} {{ dimmensionUnitSystemLabel }} </template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="pa-1" color="grey darken-2" @click.stop.prevent="itemDelete(item)">mdi-trash-can-outline</v-icon>
      </template>

      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Reset</v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog.show" max-width="740px" :persistent="false">
      <v-card>
        <v-card-title>
          <span class="title1"><lang :t="dialog.title"></lang></span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="isFormValid">
            <BoxItem
              :title="$t('LHF-LABEL-NAME')"
              :value="editedItem.name"
              :rules="[(v) => !!v || $t('GLOBAL-FORM-FIELD_REQUIRED')]"
              :required="true"
              @updateValue="editedItem.name = $event"
            />
            <BoxItem
              :title="$t('LHF-LABEL-TYPE')"
              :value="editedItem.type"
              :items="types"
              itemsValue="value"
              itemsText="name"
              @updateValue="editedItem.type = $event"
            />
            <v-row>
              <v-col>
                <BoxItem
                  :title="$t('LHF-LABEL-WIDTH_MM')"
                  :value="editedItem.width_mm"
                  :rules="[(v) => !!v || $t('GLOBAL-FORM-FIELD_REQUIRED')]"
                  :required="true"
                  @updateValue="editedItem.width_mm = Number($event)"
                />
              </v-col>
              <v-col>
                <BoxItem
                  :title="$t('LHF-LABEL-HEIGHT_MM')"
                  :value="editedItem.height_mm"
                  :rules="[(v) => !!v || $t('GLOBAL-FORM-FIELD_REQUIRED')]"
                  :required="true"
                  @updateValue="editedItem.height_mm = Number($event)"
                />
              </v-col>
              <v-col>
                <BoxItem
                  :title="$t('LHF-LABEL-LENGTH_MM')"
                  :value="editedItem.length_mm"
                  :rules="[(v) => !!v || $t('GLOBAL-FORM-FIELD_REQUIRED')]"
                  :required="true"
                  @updateValue="editedItem.length_mm = Number($event)"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeModal" :disabled="dialog.isSaving"><lang t="GLOBAL-BTN-CANCEL"></lang></v-btn>
          <v-btn elevation="0" class="general-action-btn" :disabled="!isFormValid" :loading="dialog.isSaving" @click="itemSave()"
            ><lang t="GLOBAL-BTN-SAVE"></lang
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Button from "../../../components/ui-elements/Button.vue";
import BoxItem from "../../../components/ui-elements/BoxItem.vue";
import DisplayNumber from "../../../components/DisplayNumber.vue";
import {converterMixin} from "../../../mixins/converterMixin";
import {cloneDeep} from 'lodash'; 

export default {
  name: 'LHFIndex',
  mixins:[converterMixin],
  components: {
    Button,
    BoxItem,
    DisplayNumber,
  },
  data: () => ({
    isSaving: false,
    isLoading: true,

    search: "",

    headers: [
      { text: "Image", align: "start", value: "image", sortable: false, class: "general-table__th--5" },
      { text: "Name", align: "start", value: "name", sortable: true, class: "general-table__th--5" },
      { text: "Type", value: "typeDisplay", sortable: true, class: "general-table__th--5" },
      { text: "Dimensions", value: "dimensions", sortable: false, class: "general-table__th--5" },
      { text: "Material", value: "materialDisplay", sortable: false, class: "general-table__th--5" },
      { text: "", value: "actions", sortable: false, width: "80px", align: "end" },
    ],

    items: [],
    defaultItems: [],

    dialog: {
      show: false,
      title: "",
      isSaving: false,
    },

    editedIndex: -1,
    widthLabel: '',
    heightLabel: '',
    lengthLabel: '',
    editedItem: {
      name: null,
      type: null,
      width_mm: null,
      height_mm: null,
      length_mm: null,
    },

    defaultItem: {
      name: null,
      type: null,
      width_mm: null,
      height_mm: null,
      length_mm: null,
    },

    isFormValid: false,
  }),

  computed: {
    types() {
      return [
        {
          name: this.$t("LHF-TYPE-PALLET"),
          value: "pallet",
        },
        {
          name: this.$t("LHF-TYPE-TRAY"),
          value: "tray",
        },
      ];
    },
    dimmensionValue(){
      return (value) => {
        if(this.isMetricUnitSystem) return value;
        return this.milimetersToInch(value);
      }
    },
    dimmensionUnitSystemLabel(){
      if(this.isMetricUnitSystem) return "mm";
      return this.imperialLabel("mm");
    }

  },

  watch: {
    "dialog.show"(val) {
      val || this.closeModal();
    },
  },

  created() {
    this.initialize();
  },

  mounted() {},

  methods: {
    closeModal() {
      this.dialog.show = false;

      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });

      this.$refs.form.resetValidation();
    },

    initialize() {
      this.isLoading = true;
      
      this.axios.get("loadcarriers").then((res) => {
        const items = res.data.filter((_) => _.warehouse_id == null);
        this.items = cloneDeep(items).map((el) => {
          el.typeDisplay = this.$t("LHF-TYPE-" + el.type.toUpperCase()); 
          el.materialDisplay = el.material ? this.$t("LHF-MATERIAL-" + el.material.toUpperCase()) : "";
          if(!this.isMetricUnitSystem){
            el.width_mm = this.dimmensionValue(el.width_mm);
            el.height_mm = this.dimmensionValue(el.height_mm);
            el.length_mm = this.dimmensionValue(el.length_mm);
          }
          
          return el;
        });
        this.defaultItems = cloneDeep(this.items);
        this.isLoading = false;
      });
    },

    itemDelete(item) {
      this.$confirm(this.$t("GLOBAL-CONFIRMATION-DELETE-MSG", { NAME: item.name }), {
        title: this.$t("GLOBAL-CONFIRMATION-DELETE-TITLE"),
        buttonTrueText: this.$t("GLOBAL-CONFIRMATION-DELETE-YES"),
        buttonFalseText: this.$t("GLOBAL-CONFIRMATION-DELETE-CANCEL"),
      }).then((res) => {
        if (res) {
          this.axios.delete("loadcarriers/" + item.id).then((res) => {
            this.initialize();
            this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-SUCCESSFULLY_DELETED") });
          });
        }
      });
    },

    itemEdit(item) {
      this.dialog.title = this.$t("LHF-EDIT_LHF");
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog.show = true;
    },

    itemAdd() {
      this.dialog.title = this.$t("LHF-NEW_LHF");
      this.dialog.show = true;
    },

    itemSave() {
      if (!this.$refs.form.validate()) {
        return false;
      }

      this.dialog.isSaving = true;
      let item = cloneDeep(this.editedItem);
      if(!this.isMetricUnitSystem){
        let foundItem = null;
        if(item.id){
          const foundItemId = this.defaultItems.findIndex((el) => el.id === item.id);
          if(foundItemId !== -1){
            foundItem = this.defaultItems[foundItemId];
          }
        }
        if(!foundItem || (foundItem && foundItem.width_mm !== item.width_mm)){
          item.width_mm = this.inchToMilimeters(item.width_mm);
        }else{
          delete item.width_mm;
        }

        if(!foundItem || (foundItem && foundItem.height_mm !== item.height_mm)){
          item.height_mm = this.inchToMilimeters(item.height_mm);
        }else{
          delete item.height_mm;
        }
        
        if(!foundItem || (foundItem && foundItem.length_mm !== item.length_mm)){
          item.length_mm = this.inchToMilimeters(item.length_mm);
        }else{
          delete item.length_mm;
        }
        
      }

      if (this.editedIndex == -1) {
        this.axios.post("loadcarriers", item).then((res) => {
          this._fin();
        });
      } else {
        this.axios.put("loadcarriers/" + item.id, item).then((res) => {
          this._fin();
        });
      }
    },

    _fin() {
      this.dialog.isSaving = false;
      this.$store.dispatch("setSnackbar", { text: this.$t("GLOBAL-MESSAGE-SUCCESSFULLY_SAVED") });
      this.initialize();
      this.closeModal();
    },
  },
};
</script>

<style lang="scss"></style>
